import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button as FPButton } from '@finpay-development/shared-components';
import {
    Box,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Button,
    Link
} from '@mui/material';
import { VOBSelector } from './sections/VOBSelector';
import { VOBFormFields } from './sections/VOBFormFields';
import { VOBFormData } from './types';
import { LevelsOfCare } from './sections/LevelsOfCare';
import { NetworkTypeSelector } from './sections/NetworkTypeSelector';
import { RateAsSelector } from './sections/RateAsSelector';
import { AvailityErrorDialog } from './sections/AvailityErrorDialog';
import { DatePicker } from '@finpay-development/shared-components';
import { useVOBForm } from './hooks/useVOBForm';
import { showErrorStatus, showStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import { downloadVobPdf } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-thunk';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningIcon from '@mui/icons-material/Warning';
import store, { AppDispatch } from '../../../../shared/state/store';
import AccessControl from '@finpay-development/shared-platform/lib/esm/security/components/access-control';
import { RolePageNames } from '@finpay-development/shared-platform/lib/esm/security/model/role-page-names';
import { clearSelectedEstimate, estimatorSlice } from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-slice';
import { RootState } from 'src/shared/state/root-reducer';
import { saveEstimateAfterVob } from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk';
import { loadAndSetRateAsPayorPlan, rematchRateAsPayorPlanToFacility, isFacilityPlanIdOnFacility } from '../../../../estimates/utils/payor-plan-utils';
import { Client, Estimate, Facility, Payor, PayorPlan, Plan, Vob } from '@finpay-estimation/estimation-types';
import { Utils } from '@finpay-development/shared-platform/lib/esm/shared/utils';
import { validateVobFormFields, VobFormFieldErrors } from './utils/vob-form-field-validation';
import { syncIocFacilityAfterVobSave } from './utils/sync-ioc-facility';
import { resolveDefaultFacilityId } from './utils/vob-utils';
import type { Employers } from '@finpay-development/shared-platform/lib/esm/meta-data/models/metaData';
import { getEmployers } from '@finpay-development/shared-platform/lib/esm/meta-data/state/meta-data-thunk';

function trimmedEmployerField(value: unknown): string {
    if (typeof value !== 'string') return '';
    return value.trim();
}

/** Patient / CRM payloads may use camelCase or snake_case for nested income. */
function employerNameFromContact(contact: unknown): string {
    if (!contact || typeof contact !== 'object') return '';
    const c = contact as {
        contactIncome?: { employer?: unknown };
        contact_income?: { employer?: unknown };
    };
    return (
        trimmedEmployerField(c.contactIncome?.employer) ||
        trimmedEmployerField(c.contact_income?.employer)
    );
}

/** Estimate Patient tab employer dropdown uses clientEmployerId + metaData.employers[clientId], not always contactIncome.employer text. */
function clientEmployerIdFromContact(contact: unknown): number | null {
    if (!contact || typeof contact !== 'object') return null;
    const c = contact as {
        contactIncome?: { clientEmployerId?: unknown };
        contact_income?: { client_employer_id?: unknown };
    };
    const raw = c.contactIncome?.clientEmployerId ?? c.contact_income?.client_employer_id;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function employerNameFromClientEmployersMap(
    clientId: number | string | undefined,
    clientEmployerId: number | null,
    employersByClient: Employers
): string {
    if (clientEmployerId == null || clientId == null || clientId === '') return '';
    const cid = Number(clientId);
    if (!Number.isFinite(cid)) return '';
    const list = employersByClient[cid];
    if (!list?.length) return '';
    const match = list.find((e) => Number(e.clientEmployerId) === Number(clientEmployerId));
    return trimmedEmployerField(match?.employerName);
}

function VOBForm({
    onAvailityRunningChange,
    onAvailityCancelReady,
}: {
    onAvailityRunningChange?: (isRunning: boolean) => void;
    onAvailityCancelReady?: (cancel: () => void) => void;
} = {}) {
    const dispatch = useDispatch<AppDispatch>();
    const vobHeaderActionsRef = React.useRef<HTMLDivElement>(null);
    const [headerActionsWidthPx, setHeaderActionsWidthPx] = React.useState(0);
    const [isSaving, setIsSaving] = React.useState(false);
    const [resetButtonStates, setResetButtonStates] = React.useState(false);
    const [buttonStates, setButtonStates] = React.useState<{
        isDeductibleButtonPressed: boolean;
        isOopmButtonPressed: boolean;
        isServiceLimitsButtonPressed: boolean;
        isMaxDaysButtonPressed: boolean;
        isCoinsuranceButtonPressed: boolean;
    } | undefined>(undefined);
    const [dateErrors, setDateErrors] = React.useState({
        effectiveFrom: false,
        effectiveTo: false
    });
    const [facilityError, setFacilityError] = React.useState(false);
    const [activePolicyError, setActivePolicyError] = React.useState(false);
    const [deductibleError, setDeductibleError] = React.useState(false);
    const [remainingError, setRemainingError] = React.useState(false);
    const [oopmError, setOopmError] = React.useState(false);
    const [oopmRemainingError, setOopmRemainingError] = React.useState(false);
    const [deductibleErrorMessage, setDeductibleErrorMessage] = React.useState('');
    const [remainingErrorMessage, setRemainingErrorMessage] = React.useState('');
    const [oopmErrorMessage, setOopmErrorMessage] = React.useState('');
    const [oopmRemainingErrorMessage, setOopmRemainingErrorMessage] = React.useState('');
    const [copayError, setCopayError] = React.useState(false);
    const [maxDaysError, setMaxDaysError] = React.useState(false);
    const [coinsuranceError, setCoinsuranceError] = React.useState(false);
    const [copayErrorMessage, setCopayErrorMessage] = React.useState('');
    const [maxDaysErrorMessage, setMaxDaysErrorMessage] = React.useState('');
    const [coinsuranceErrorMessage, setCoinsuranceErrorMessage] = React.useState('');

    const [levelErrors, setLevelErrors] = React.useState<Map<number, {
        deductibleTotalError?: boolean;
        deductibleRemainingError?: boolean;
        oopmTotalError?: boolean;
        oopmRemainingError?: boolean;
        copayError?: boolean;
        maxDaysError?: boolean;
        coinsuranceError?: boolean;
        deductibleTotalErrorMessage?: string;
        deductibleRemainingErrorMessage?: string;
        oopmTotalErrorMessage?: string;
        oopmRemainingErrorMessage?: string;
        copayErrorMessage?: string;
        maxDaysErrorMessage?: string;
        coinsuranceErrorMessage?: string;
    }>>(new Map());

    const levelsOfCare = useSelector(
        (state: RootState) => state.estimatorContext.facilityLevelsOfCareList
    );

    /** Dropdown display value only; Redux rateAsPayorPlan is not updated */
    const [selectedRateAsFacilityPlanId, setSelectedRateAsFacilityPlanId] = React.useState<number | null>(null);

    const estimate = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate
    );
    const selectedEncounter = useSelector(
        (state: RootState) => state.patientContext.selectedEncounter
    );
    const isSelfPay = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate?.isSelfPay || false
    );
    const isPrivatePay = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate?.isPrivatePay || false
    );
    const reduxRateAsPayorMap = useSelector(
        (state: RootState) => state.estimatorContext.rateAsPayorMap
    ) || {};
    const reduxMasterPayors = useSelector(
        (state: RootState) => state.adminContext.adminConfigurationContext?.payersMap
    ) || new Map();
    const inboundPtRecord = useSelector((state: RootState) => state.patientContext.inboundPtRecord);
    const employersByClient = useSelector((state: RootState) => state.metaData.employers);
    const {
        // States
        activePolicy,
        carryover,
        selectedVOB,
        memberId,
        facilities,
        selectedFacility,
        facilitiesLoading,
        networkType,
        memberType,
        liveVOB,
        formData,
        currentVob,
        isRunningCRMVOB,
        isRunningAvailityVOB,
        cancelAvailityVob,
        isLoadingRunAvailityVOBButton,
        effectiveFromDate,
        effectiveToDate,
        facilityPayersState,
        rateAsPayorPlan,
        isPayerMatched,
        comparisonError,
        selectedPatient,
        selectedPatientCurrentInsurance,
        payersMap,
        estimateId,
        savedLOCDataMap,
        savedFormDataMap,
        showAvailityErrorDialog,
        availityErrorMessage,
        isReadOnlyAfterConvert,
        // Handlers
        handleInputChange,
        handleVOBSelectChange,
        handleNetworkTypeChange,
        handleMemberTypeChange,
        handleEffectiveFromDateChange,
        handleEffectiveToDateChange,
        handleFacilityChange,
        setSelectedFacility,
        setActivePolicy,
        setCarryover,
        setLiveVOB,
        runCRMVob,
        runAvailityVob,
        runAvailityVobWithOverrides,
        handleSaveVOB: saveVOB,
        setSavedFormDataMap,
        setSavedLOCDataMap,
        setShowAvailityErrorDialog,
        vobFieldValidationNonce,
    } = useVOBForm(
        () => setResetButtonStates(true),
        setButtonStates,
        (facilityPayersStateArg, payersMapArg, rateAsPayorPlanArg) => {
            if (selectedRateAsFacilityPlanId != null && facilityPayersStateArg?.length) {
                const payer = facilityPayersStateArg.find((p: any) =>
                    p.facilityPayorPlans?.some((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(selectedRateAsFacilityPlanId))
                );
                const plan = payer?.facilityPayorPlans?.find((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(selectedRateAsFacilityPlanId));
                if (payer && plan) {
                    const payorName = payer.facilityPayorName || payersMapArg?.get(payer.cfgPayorId)?.payorName;
                    const planName = plan.facilityPayorPlanName || payersMapArg?.get(payer.cfgPayorId)?.payorPlans?.find((p: any) => Number(p.payorPlanId) === Number(plan.cfgPayorPlanId))?.planName;
                    return {
                        payor: { payorId: payer.cfgPayorId, facilityPayorName: payer.facilityPayorName, payorName, inNetwork: payer.inNetwork },
                        plan: { payorPlanId: plan.cfgPayorPlanId, facilityPlanId: plan.facilityPayorPlanId, facilityPlanName: plan.facilityPayorPlanName, planName },
                        isOverridden: true,
                    } as PayorPlan;
                }
            }
            if (rateAsPayorPlanArg) {
                const vobRateAs = (currentVob as Vob & { rateAsPayorPlan?: PayorPlan })?.rateAsPayorPlan;
                const isUserOverride =
                    selectedRateAsFacilityPlanId != null ||
                    rateAsPayorPlanArg.isOverridden === true ||
                    vobRateAs?.isOverridden === true;
                const facilityPlanId = rateAsPayorPlanArg.plan?.facilityPlanId;
                if (
                    facilityPayersStateArg?.length &&
                    facilityPlanId != null &&
                    !isFacilityPlanIdOnFacility(facilityPayersStateArg, facilityPlanId)
                ) {
                    const rematched = rematchRateAsPayorPlanToFacility(
                        rateAsPayorPlanArg,
                        facilityPayersStateArg,
                        payersMapArg
                    );
                    if (rematched) {
                        return {
                            ...rematched,
                            isOverridden: isUserOverride || rematched.isOverridden === true,
                        } as PayorPlan;
                    }
                }
                return {
                    ...rateAsPayorPlanArg,
                    isOverridden: isUserOverride,
                } as PayorPlan;
            }
            return undefined;
        }
    );

    React.useEffect(() => {
        onAvailityRunningChange?.(isRunningAvailityVOB);
    }, [isRunningAvailityVOB, onAvailityRunningChange]);

    React.useEffect(() => {
        onAvailityCancelReady?.(cancelAvailityVob);
    }, [cancelAvailityVob, onAvailityCancelReady]);

    const employerResolutionClientId = React.useMemo(() => {
        const raw =
            selectedPatient?.clientId ??
            currentVob?.client?.clientId ??
            inboundPtRecord?.clientId;
        if (raw == null || raw === '') return NaN;
        const n = Number(raw);
        return Number.isFinite(n) ? n : NaN;
    }, [selectedPatient?.clientId, currentVob?.client?.clientId, inboundPtRecord?.clientId]);

    const employersForClient =
        Number.isFinite(employerResolutionClientId) && employerResolutionClientId > 0
            ? employersByClient[employerResolutionClientId]
            : undefined;

    React.useEffect(() => {
        if (!Number.isFinite(employerResolutionClientId) || employerResolutionClientId <= 0) return;
        if (employersForClient !== undefined) return;
        void dispatch(getEmployers(employerResolutionClientId));
    }, [dispatch, employerResolutionClientId, employersForClient]);

    const navigate = useNavigate();

    const vobGroupNameLabel = currentVob?.groupName?.trim() ?? '';
    const vobGroupNumLabel =
        currentVob?.groupNum?.trim() ||
        selectedPatientCurrentInsurance?.groupNumber?.trim() ||
        '';

    const planSponsorDisplayLine = (() => {
        const ps = currentVob?.planSponsor;
        if (!ps) return '';
        const sponsorName = ps.planSponsorName?.trim() ?? '';
        const sponsorId = ps.planSponsorId?.trim() ?? '';
        if (sponsorName && sponsorId) return `Sponsor: ${sponsorName}, ${sponsorId}`;
        if (sponsorName) return `Sponsor: ${sponsorName}`;
        if (sponsorId) return `Sponsor: ${sponsorId}`;
        return '';
    })();

    const vobGroupDisplayLine =
        vobGroupNameLabel && vobGroupNumLabel
            ? `Group: ${vobGroupNameLabel}, ${vobGroupNumLabel}`
            : vobGroupNameLabel
              ? `Group: ${vobGroupNameLabel}`
              : vobGroupNumLabel
                ? `Group: ${vobGroupNumLabel}`
                : '';

    const employerDisplayLine = (() => {
        const vobName = currentVob?.employer?.employerName?.trim() ?? '';
        const patientEmployerName =
            employerNameFromContact(selectedPatient?.contact) ||
            employerNameFromContact(inboundPtRecord?.patient?.contact);
        const clientEmployerId =
            clientEmployerIdFromContact(selectedPatient?.contact) ||
            clientEmployerIdFromContact(inboundPtRecord?.patient?.contact);
        const clientIdForEmployerLookup =
            selectedPatient?.clientId ?? currentVob?.client?.clientId ?? inboundPtRecord?.clientId;
        const fromClientEmployersCatalog = employerNameFromClientEmployersMap(
            clientIdForEmployerLookup,
            clientEmployerId,
            employersByClient
        );
        const inboundVobEmployerName = trimmedEmployerField(
            (inboundPtRecord as { vob?: { employer?: { employerName?: unknown } } })?.vob?.employer
                ?.employerName
        );
        const displayName =
            vobName ||
            patientEmployerName ||
            fromClientEmployersCatalog ||
            inboundVobEmployerName;
        if (!displayName) return '';
        return `Employer: ${displayName}`;
    })();

    const subscriberFirst = currentVob?.subscriber?.firstName?.trim() ?? '';
    const subscriberLast = currentVob?.subscriber?.lastName?.trim() ?? '';
    const subscriberRelationshipLabel = currentVob?.patient?.subscriberRelationship?.trim() ?? '';
    const subscriberDisplayLine =
        subscriberFirst && subscriberLast
            ? subscriberRelationshipLabel
                ? `Subscriber: ${subscriberFirst} ${subscriberLast}, ${subscriberRelationshipLabel}`
                : `Subscriber: ${subscriberFirst} ${subscriberLast}`
            : '';

    const payerLineNetworkIdSuffix = (() => {
        const planNetworkId = currentVob?.plan?.planNetworkId?.trim();
        if (planNetworkId) return ` Network ID: ${planNetworkId}`;
        return '';
    })();

    const payerPhoneDisplay = (() => {
        const phone = currentVob?.payor?.phone?.trim();
        if (phone) return Utils.formatPhoneNumber(phone);
        return '';
    })();

    const payerPortalUrl = (() => {
        const raw = currentVob?.payor?.url?.trim();
        if (!raw) return '';
        return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    })();

    const payerNameText = (() => {
        if (currentVob?.payor?.payorId) {
            return payersMap?.get(currentVob.payor.payorId)?.payorName || currentVob.payor.payorName || '';
        }
        if (selectedPatientCurrentInsurance?.payorId && payersMap?.get(selectedPatientCurrentInsurance.payorId)?.payorName) {
            return payersMap.get(selectedPatientCurrentInsurance.payorId)?.payorName || '';
        }
        return '';
    })();

    const payerPlanText = (() => {
        if (currentVob?.payor?.payorId) {
            const planName = currentVob.plan?.payorPlanId &&
                payersMap?.get(currentVob.payor.payorId)?.payorPlans?.find(
                    plan => Number(plan.payorPlanId) === Number(currentVob.plan?.payorPlanId)
                )?.planName;
            if (planName) return `/${planName}`;
            if (currentVob.plan?.planName) return `/${currentVob.plan.planName}`;
            return '';
        }
        if (selectedPatientCurrentInsurance?.payorId && payersMap?.get(selectedPatientCurrentInsurance.payorId)?.payorName) {
            const planName = selectedPatientCurrentInsurance?.payorPlanId &&
                payersMap?.get(selectedPatientCurrentInsurance.payorId)?.payorPlans?.find(
                    plan => Number(plan.payorPlanId) === selectedPatientCurrentInsurance.payorPlanId
                )?.planName;
            if (planName) return `/${planName}`;
            return '';
        }
        return '';
    })();

    const payerLineInsuranceSuffix = (() => {
        const type = currentVob?.insuranceType?.trim();
        const code = currentVob?.insuranceTypeCode?.trim();
        if (type) return ` — ${type}`;
        if (code) return ` — ${code}`;
        return '';
    })();

    /** On Rate as dropdown change: sync tab (IN/OUT), then load facility payers and set rateAsPayorPlan in Redux so Estimate page has latest data */
    const handleRateAsPayorPlanChangeTabOnly = React.useCallback((facilityPlanId: number) => {
        setSelectedRateAsFacilityPlanId(facilityPlanId);
        const payer = facilityPayersState?.find((p) =>
            p.facilityPayorPlans?.some((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(facilityPlanId))
        );
        const plan = payer?.facilityPayorPlans?.find((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(facilityPlanId));
        if (payer != null) {
            const newNetworkType = payer.inNetwork ? 'in' : 'out';
            if (newNetworkType !== networkType) {
                handleNetworkTypeChange(newNetworkType);
            }
        }
        if (payer && plan) {
            const payorObj: Payor = {
                payorId: payer.cfgPayorId,
                facilityPayorName: payer.facilityPayorName,
                payorName: payer.facilityPayorName || reduxMasterPayors?.get(payer.cfgPayorId)?.payorName,
                inNetwork: payer.inNetwork
            } as Payor;
            const planObj: Plan = {
                payorPlanId: plan.cfgPayorPlanId,
                facilityPlanId: plan.facilityPayorPlanId,
                facilityPlanName: plan.facilityPayorPlanName,
                planName: plan.facilityPayorPlanName || reduxMasterPayors?.get(payer.cfgPayorId)?.payorPlans?.find((p: { payorPlanId: number }) => Number(p.payorPlanId) === Number(plan.cfgPayorPlanId))?.planName
            } as Plan;
            const clientObj: Client = currentVob?.client ?? { clientId: Number(selectedPatient.clientId) } as Client;
            const facilityObj = currentVob?.facility ?? (selectedFacility ? { facilityId: Number(selectedFacility) } as Facility : undefined);
            if (facilityObj?.facilityId) {
                // Do not pass selectedVob so findAndSetRateAsPayorPlan uses the new dropdown selection
                // (otherwise it would use selectedVob.rateAsPayorPlan when isOverridden and ignore the change)
                loadAndSetRateAsPayorPlan(
                    clientObj,
                    facilityObj,
                    dispatch,
                    reduxMasterPayors,
                    payorObj,
                    planObj,
                    reduxRateAsPayorMap,
                    estimate ?? undefined,
                    rateAsPayorPlan ?? undefined,
                    undefined,
                    undefined,
                    currentVob?.vobId,
                    true
                )
                    .then(() => {
                        dispatch(
                            estimatorSlice.actions.setRateAsPayorPlan({
                                payor: payorObj,
                                plan: planObj,
                                isOverridden: true,
                            } as PayorPlan)
                        );
                    })
                    .catch((err) => {
                        console.error('loadAndSetRateAsPayorPlan on Rate as change:', err);
                    });
            }
        }
    }, [facilityPayersState, networkType, handleNetworkTypeChange, reduxMasterPayors, reduxRateAsPayorMap, dispatch, currentVob, selectedPatient.clientId, selectedFacility, estimate, rateAsPayorPlan]);

    React.useEffect(() => {
        setSelectedRateAsFacilityPlanId(null);
    }, [selectedFacility, currentVob?.vobId]);

    // Prefer rateAsPayorPlan from current VOB (API or vob.payor + vob.plan) for dropdown display
    const vobRateAsPayorPlan = React.useMemo(() => {
        if (!currentVob) return null;
        const fromVob = (currentVob as Vob & { rateAsPayorPlan?: { payor: Payor; plan: Plan | null } })?.rateAsPayorPlan;
        if (fromVob?.payor && fromVob?.plan != null) return { payor: fromVob.payor, plan: fromVob.plan };
        if (currentVob.payor && currentVob.plan) return { payor: currentVob.payor as Payor, plan: currentVob.plan as Plan };
        return null;
    }, [currentVob]);

    React.useEffect(() => {
        if (resetButtonStates) {
            // Reset the flag after the next render cycle
            const timer = setTimeout(() => {
                setResetButtonStates(false);
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [resetButtonStates]);

    const applyVobFieldErrors = React.useCallback((result: VobFormFieldErrors) => {
        setFacilityError(result.facilityError);
        setActivePolicyError(result.activePolicyError);
        setDateErrors(result.dateErrors);
        setDeductibleError(result.deductibleError);
        setRemainingError(result.remainingError);
        setOopmError(result.oopmError);
        setOopmRemainingError(result.oopmRemainingError);
        setDeductibleErrorMessage(result.deductibleErrorMessage);
        setRemainingErrorMessage(result.remainingErrorMessage);
        setOopmErrorMessage(result.oopmErrorMessage);
        setOopmRemainingErrorMessage(result.oopmRemainingErrorMessage);
        setCopayError(result.copayError);
        setMaxDaysError(result.maxDaysError);
        setCoinsuranceError(result.coinsuranceError);
        setCopayErrorMessage(result.copayErrorMessage);
        setMaxDaysErrorMessage(result.maxDaysErrorMessage);
        setCoinsuranceErrorMessage(result.coinsuranceErrorMessage);
        setLevelErrors(result.levelErrors);
    }, []);

    const latestVobValidationInputRef = React.useRef({
        selectedFacility,
        skipFacilityError: false,
        effectiveFromDate,
        effectiveToDate,
        activePolicy,
        formData,
        levelsOfCare,
        savedLOCDataMap,
        networkType,
        memberType,
    });
    latestVobValidationInputRef.current = {
        selectedFacility,
        skipFacilityError:
            facilitiesLoading ||
            (!selectedFacility && currentVob?.facility?.facilityId != null),
        effectiveFromDate,
        effectiveToDate,
        activePolicy,
        formData,
        levelsOfCare,
        savedLOCDataMap,
        networkType,
        memberType,
    };

    React.useEffect(() => {
        if (isReadOnlyAfterConvert) return;
        applyVobFieldErrors(validateVobFormFields(latestVobValidationInputRef.current));
    }, [
        applyVobFieldErrors,
        isReadOnlyAfterConvert,
        vobFieldValidationNonce,
        selectedFacility,
        facilitiesLoading,
        currentVob?.facility?.facilityId,
        networkType,
        memberType,
        effectiveFromDate,
        effectiveToDate,
        activePolicy,
        levelsOfCare,
    ]);

    React.useLayoutEffect(() => {
        const el = vobHeaderActionsRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return;
        }
        const update = () => setHeaderActionsWidthPx(el.offsetWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [isSaving]);

    const handleSaveVOB = async () => {
        if (isReadOnlyAfterConvert) return;
        setIsSaving(true);
        try {
            const validation = validateVobFormFields({
                selectedFacility,
                effectiveFromDate,
                effectiveToDate,
                activePolicy,
                formData,
                levelsOfCare,
                savedLOCDataMap,
                networkType,
                memberType,
            });
            applyVobFieldErrors(validation);

            if (validation.hasNegative) {
                setIsSaving(false);
                dispatch(showErrorStatus('Please correct negative values'));
                return;
            }

            if (validation.hasCoinsuranceOver100) {
                setIsSaving(false);
                dispatch(showErrorStatus('Please correct validation errors'));
                return;
            }

            if (validation.hasError || validation.hasLevelErrors) {
                const finalErrorMessage = validation.hasLevelErrors ?
                    `${validation.errorMessage} Please check Levels of Care for validation errors.` :
                    validation.errorMessage;
                dispatch(showErrorStatus(finalErrorMessage));
                setIsSaving(false);
                return;
            }
            
            try {
                const saveSuccess = await saveVOB();
                if (saveSuccess) {
                    const savedVob = saveSuccess as Vob;
                    const facilityId = Number(
                        savedVob.facility?.facilityId ?? selectedFacility
                    );
                    const encounterId =
                        estimate?.patientEncounterId ??
                        selectedEncounter?.patientEncounterId;
                    let estimateSaveFailed = false;

                    if (estimate && estimate.estimateId) {
                        try {
                            const newVobId = savedVob.vobId ?? currentVob?.vobId!;
                            const changingFromFacility = (currentVob?.facility.facilityId !== selectedFacility);
                            // if we had changes to the facility or payor we need to run loadandset
                            if(changingFromFacility) {
                                const clientId = Number(selectedPatient.clientId);
                                const clientObj = { clientId } as Client;
                                const currentFacility = { facilityId: Number(selectedFacility) };
                                let updatedEstimate: Estimate = {
                                    ...Utils.deepClone(estimate),
                                    facilityId: selectedFacility,
                                };
                                const savedRateAs = (savedVob as Vob & { rateAsPayorPlan?: PayorPlan })?.rateAsPayorPlan;
                                const selectedPayor: Payor = {
                                    payorId: savedRateAs?.payor?.payorId
                                        ?? currentVob?.payor?.payorId
                                        ?? selectedPatientCurrentInsurance?.payorId
                                };
                                const selectedPlan: Plan = {
                                    payorPlanId: savedRateAs?.plan?.payorPlanId
                                        ?? currentVob?.plan?.payorPlanId
                                        ?? selectedPatientCurrentInsurance?.payorPlanId,
                                    ...(savedRateAs?.plan?.facilityPlanId != null && {
                                        facilityPlanId: savedRateAs.plan.facilityPlanId,
                                    }),
                                };
                                await loadAndSetRateAsPayorPlan(
                                    clientObj,
                                    currentFacility,
                                    dispatch,
                                    reduxMasterPayors,
                                    selectedPayor,
                                    selectedPlan,
                                    reduxRateAsPayorMap,
                                    estimate!,
                                    rateAsPayorPlan,
                                    savedVob,
                                    updatedEstimate!,
                                    newVobId!,
                                    true
                                );
                            } else {
                              // NO facility changes: still rematch Estimate LOCs when Rate as changed
                                // When vob.rateAsPayorPlan.isOverridden is true, use vob's Rate as for estimate; else fall back to Redux rateAsPayorPlan
                                const rateAsPayorPlanForEstimate = ((): PayorPlan | null | undefined => {
                                    if (selectedRateAsFacilityPlanId != null && facilityPayersState?.length) {
                                        const payer = facilityPayersState.find((p: any) =>
                                            p.facilityPayorPlans?.some((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(selectedRateAsFacilityPlanId))
                                        );
                                        const plan = payer?.facilityPayorPlans?.find((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(selectedRateAsFacilityPlanId));
                                        if (payer && plan) {
                                            const payorName = payer.facilityPayorName || reduxMasterPayors?.get(payer.cfgPayorId)?.payorName;
                                            const planName = plan.facilityPayorPlanName || reduxMasterPayors?.get(payer.cfgPayorId)?.payorPlans?.find((p: any) => Number(p.payorPlanId) === Number(plan.cfgPayorPlanId))?.planName;
                                            return {
                                                payor: { payorId: payer.cfgPayorId, facilityPayorName: payer.facilityPayorName, payorName, inNetwork: payer.inNetwork } as Payor,
                                                plan: { payorPlanId: plan.cfgPayorPlanId, facilityPlanId: plan.facilityPayorPlanId, facilityPlanName: plan.facilityPayorPlanName, planName } as Plan,
                                                isOverridden: true,
                                            } as PayorPlan;
                                        }
                                    }
                                    const savedVobRateAs = (savedVob as Vob & { rateAsPayorPlan?: PayorPlan })?.rateAsPayorPlan;
                                    if (savedVobRateAs?.isOverridden === true && savedVobRateAs?.payor && savedVobRateAs?.plan) {
                                        return savedVobRateAs;
                                    }
                                    const vobRateAs = (currentVob as Vob & { rateAsPayorPlan?: PayorPlan })?.rateAsPayorPlan;
                                    if (vobRateAs?.isOverridden === true && vobRateAs?.payor && vobRateAs?.plan) {
                                        return vobRateAs;
                                    }
                                    if (rateAsPayorPlan?.payor && rateAsPayorPlan?.plan) {
                                        return {
                                            ...rateAsPayorPlan,
                                            isOverridden:
                                                selectedRateAsFacilityPlanId != null ||
                                                rateAsPayorPlan.isOverridden === true,
                                        } as PayorPlan;
                                    }
                                    return undefined;
                                })();

                                const previousRateAs = estimate?.rateAsPayorPlan as PayorPlan | undefined;
                                const rateAsChanged = (() => {
                                    if (!rateAsPayorPlanForEstimate?.payor || !rateAsPayorPlanForEstimate?.plan) {
                                        return false;
                                    }
                                    if (!previousRateAs?.payor || !previousRateAs?.plan) {
                                        return true;
                                    }
                                    const prevFacilityPlanId = previousRateAs.plan.facilityPlanId;
                                    const nextFacilityPlanId = rateAsPayorPlanForEstimate.plan.facilityPlanId;
                                    if (prevFacilityPlanId != null && nextFacilityPlanId != null) {
                                        return Number(prevFacilityPlanId) !== Number(nextFacilityPlanId);
                                    }
                                    return (
                                        Number(previousRateAs.payor.payorId) !== Number(rateAsPayorPlanForEstimate.payor.payorId) ||
                                        Number(previousRateAs.plan.payorPlanId) !== Number(rateAsPayorPlanForEstimate.plan.payorPlanId)
                                    );
                                })();

                                const putEstimateBody = {
                                    ...estimate,
                                    patientId: selectedPatient.patientId,
                                    vobId: newVobId,
                                    ...(rateAsPayorPlanForEstimate && { rateAsPayorPlan: rateAsPayorPlanForEstimate }),
                                    isSelfPay: isSelfPay,
                                    isPrivatePay: isPrivatePay,
                                    description: estimate?.description || ''
                                };

                                if (rateAsChanged && rateAsPayorPlanForEstimate) {
                                    const clientObj: Client = savedVob.client ?? currentVob?.client ?? { clientId: Number(selectedPatient.clientId) } as Client;
                                    const facilityObj: Facility = savedVob.facility ?? currentVob?.facility ?? { facilityId: Number(selectedFacility) } as Facility;
                                    const updatedEstimate: Estimate = {
                                        ...estimate,
                                        ...putEstimateBody,
                                        rateAsPayorPlan: rateAsPayorPlanForEstimate,
                                    };
                                    await loadAndSetRateAsPayorPlan(
                                        clientObj,
                                        facilityObj,
                                        dispatch,
                                        reduxMasterPayors,
                                        rateAsPayorPlanForEstimate.payor,
                                        rateAsPayorPlanForEstimate.plan,
                                        reduxRateAsPayorMap,
                                        estimate!,
                                        rateAsPayorPlan,
                                        undefined,
                                        updatedEstimate,
                                        newVobId!,
                                        true
                                    );
                                    if (!estimate?.facilityLevelOfCare?.length) {
                                        await dispatch(saveEstimateAfterVob({
                                            estimateId: estimate?.estimateId,
                                            estimate: putEstimateBody,
                                            isSelfPay: isSelfPay,
                                            isPrivatePay: isPrivatePay
                                        }));
                                    }
                                } else {
                                    await dispatch(saveEstimateAfterVob({
                                        estimateId: estimate?.estimateId,
                                        estimate: putEstimateBody,
                                        isSelfPay: isSelfPay,
                                        isPrivatePay: isPrivatePay
                                    }));
                                }
                                }
                        } catch (estimateError) {
                            estimateSaveFailed = true;
                            console.error('Error saving estimate after VOB:', estimateError);
                        }
                    }

                    // After estimate rematch/PUT, Redux selectedEstimate has the recalculated PFR.
                    let pfrAmt: number | undefined;
                    if (!estimateSaveFailed && estimate?.estimateId) {
                        const latestEstimate = store.getState().estimatorContext.selectedEstimate;
                        const isGreen = Number(selectedEncounter?.pfrEstimateId) === Number(estimate.estimateId);
                        const isConverted = Number(selectedEncounter?.workflow?.workflowId) === 3;
                        if (
                            isGreen &&
                            !isConverted &&
                            Number(latestEstimate?.estimateId) === Number(estimate.estimateId) &&
                            latestEstimate?.summary?.totalPFR != null
                        ) {
                            pfrAmt = Number(latestEstimate.summary.totalPFR);
                        }
                    }

                    const syncResult = await syncIocFacilityAfterVobSave(
                        dispatch,
                        Number(encounterId),
                        facilityId,
                        pfrAmt
                    );
                    if (!syncResult.success) {
                        dispatch(
                            showErrorStatus(
                                estimateSaveFailed
                                    ? 'VOB saved, but estimate and IOC facility sync failed'
                                    : estimate?.estimateId
                                      ? 'VOB and estimate saved, but IOC facility sync failed'
                                      : syncResult.errorMessage ||
                                        'VOB saved, but IOC facility sync failed'
                            )
                        );
                        return;
                    }

                    if (estimateSaveFailed) {
                        dispatch(showStatus('VOB and IOC saved, but estimate save failed'));
                    } else if (estimate && estimate.estimateId) {
                        dispatch(showStatus('VOB, Estimate, and IOC saved successfully'));
                    } else {
                        dispatch(showStatus('VOB and IOC saved successfully'));
                    }
                }
            } catch (error) {
                dispatch(showStatus('VOB saved failed'));
            }
        } finally {
            setIsSaving(false);
        }
    };


    const handleDownloadPdf = () => {
        if (currentVob?.vobId) {
            dispatch(downloadVobPdf(currentVob.vobId));
        }
    };

    return (
        <Box test-id="vob-form-container">
            {isReadOnlyAfterConvert && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                    test-id="vob-read-only-message"
                >
                    This VOB is read-only because the estimate has been converted to an Instance of Care.
                </Typography>
            )}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs sx={{ minWidth: 0 }}>
                    <VOBSelector
                        selectedVOB={selectedVOB}
                        onVOBChange={handleVOBSelectChange}
                        rateAsPayorPlan={rateAsPayorPlan}
                        facilities={facilities}
                    />
                </Grid>
                <Grid item sx={{ 
                    minWidth: '200px',
                    flexShrink: 0,
                    flexGrow: 0,
                    width: 'auto',
                    paddingTop: "0px !important"
                }}>
                    <Box
                        ref={vobHeaderActionsRef}
                        test-id="vob-form-actions"
                        sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'nowrap',
                            minWidth: 'fit-content',
                            width: 'fit-content',
                        }}
                    >
                        <FPButton
                            test-id="vob-new-button"
                            onClick={async () => {
                                if (selectedPatient?.patientId) {
                                    const now = new Date();
                                    const year = now.getFullYear();
                                    setActivePolicy(true);
                                    setCarryover(false);
                                    setLiveVOB(false);
                                    handleEffectiveFromDateChange(new Date(`${year}-01-01`));
                                    handleEffectiveToDateChange(new Date(`${year}-12-31`));
                                    handleInputChange('deductible', '');
                                    handleInputChange('remaining', '');
                                    handleInputChange('oopm', '');
                                    handleInputChange('oopmRemaining', '');
                                    handleInputChange('copay', '');
                                    handleInputChange('maxDays', '');
                                    handleInputChange('coinsurance', '');
                                    handleInputChange('notes', '');
                                    handleInputChange('isDedIncOOPM', true);
                                    handleVOBSelectChange('');
                                    setResetButtonStates(true);
                                    setSavedFormDataMap(new Map());
                                    setSavedLOCDataMap(new Map());
                                    dispatch(clearSelectedEstimate());
                                    navigate(`/patients/${selectedPatient.patientId}/vob`);
                                    const newVobFacilityId = resolveDefaultFacilityId(
                                        facilities,
                                        selectedEncounter?.facilityId
                                    );
                                    if (newVobFacilityId != null) {
                                        setSelectedFacility(newVobFacilityId);
                                    }
                                    if (newVobFacilityId && selectedPatient?.clientId) {
                                        const client = { clientId: Number(selectedPatient.clientId) } as Client;
                                        const facility = { facilityId: Number(newVobFacilityId) };
                                        const payor = {
                                            payorId: selectedPatientCurrentInsurance?.payorId
                                        } as Payor;
                                        const plan = {
                                            payorPlanId: selectedPatientCurrentInsurance?.payorPlanId
                                        } as Plan;
                                        await loadAndSetRateAsPayorPlan(
                                            client,
                                            facility,
                                            dispatch,
                                            reduxMasterPayors,
                                            payor,
                                            plan,
                                            reduxRateAsPayorMap
                                        );
                                    }
                                }
                            }}
                            style={{ 
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                        >
                            New
                        </FPButton>
                        {!isReadOnlyAfterConvert && (
                            <FPButton
                                test-id="vob-save-button"
                                onClick={handleSaveVOB}
                                disabled={isSaving}
                                style={{ 
                                    minWidth: 'fit-content',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}
                                // startIcon={isSaving ? <CircularProgress size={20} /> : null}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </FPButton>
                        )}
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 0.5 }} alignItems="center">
                <Grid item xs sx={{ minWidth: 0 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="facility-select-label">
                            {facilitiesLoading || (!selectedFacility && facilities.length === 0) ? 'Loading...' : 'Facility'}
                        </InputLabel>
                        <Select
                            test-id="vob-facility-select"
                            labelId="facility-select-label"
                            id="facility-select"
                            value={selectedFacility}
                            label={facilitiesLoading || (!selectedFacility && facilities.length === 0) ? 'Loading...' : 'Facility'}
                            onChange={(e) => handleFacilityChange(Number(e.target.value))}
                            error={facilityError}
                            disabled={isReadOnlyAfterConvert || facilitiesLoading || (!selectedFacility && facilities.length === 0)}
                            className={`state-field ${facilityError ? 'error-border' : 'required-fields'}`}
                        >
                            {facilitiesLoading || (!selectedFacility && facilities.length === 0) ? (
                                <MenuItem disabled>Loading facilities...</MenuItem>
                            ) : (
                                facilities.map((facility) => (
                                    <MenuItem key={facility.facilityId} value={facility.facilityId}>
                                        {facility.facilityName}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid
                    item
                    sx={{
                        minWidth:
                            headerActionsWidthPx > 0
                                ? headerActionsWidthPx + 16
                                : 216,
                        flexShrink: 0,
                        flexGrow: 0,
                        width: 'auto',
                    }}
                >
                    {isLoadingRunAvailityVOBButton ? (
                        <FPButton
                            onClick={runAvailityVob}
                            disabled={isRunningAvailityVOB}
                            test-id="run-availability-vob"
                            style={{
                                ...(headerActionsWidthPx > 0
                                    ? {
                                          width: headerActionsWidthPx,
                                          boxSizing: 'border-box',
                                      }
                                    : {}),
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                        >
                            Run Availity VOB
                        </FPButton>
                    ) : null}
                </Grid>
            </Grid>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    mb: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            flex: '1 1 auto',
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            component="div"
                            test-id="vob-payer-display"
                            variant="body1"
                            sx={{
                                mr: 2,
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                columnGap: 0.5,
                                rowGap: 0.25,
                                lineHeight: 1.5,
                            }}
                        >
                            <Box component="span">
                                Payer:{' '}
                                {payerPortalUrl && payerNameText ? (
                                    <Link
                                        test-id="vob-payer-portal-link"
                                        href={payerPortalUrl}
                                        target="payorPortal"
                                        rel="noopener noreferrer"
                                    >
                                        {payerNameText}
                                    </Link>
                                ) : (
                                    payerNameText
                                )}
                                {payerPlanText}
                                {payerLineInsuranceSuffix}
                                {payerLineNetworkIdSuffix}
                            </Box>
                            {payerPhoneDisplay ? (
                                <Box
                                    component="span"
                                    test-id="vob-payer-phone"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    <PhoneIcon sx={{ fontSize: '1.5rem', display: 'block' }} aria-hidden />
                                    {payerPhoneDisplay}
                                </Box>
                            ) : null}
                        </Typography>
                        {comparisonError && (
                            <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                ({comparisonError})
                            </Typography>
                        )}
                    </Box>
                    <FormControlLabel
                        test-id="vob-live-vob-checkbox"
                        sx={{ flexShrink: 0, marginLeft: 'auto', mb: 0, mt: 0 }}
                        control={
                            <Checkbox
                                test-id="vob-live-vob-checkbox-input"
                                checked={liveVOB}
                                disabled={isReadOnlyAfterConvert}
                                onChange={(e) => setLiveVOB(e.target.checked)}
                            />
                        }
                        label="Live VOB"
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        columnGap: 2,
                        rowGap: 0.5,
                        /* Pull toward payer row: body1 line-height leaves ~extra below baseline; small mt was invisible */
                        mt: -0.75,
                    }}
                >
                    {planSponsorDisplayLine ? (
                        <Typography
                            variant="body1"
                            component="span"
                            test-id="vob-sponsor-display"
                            sx={{ lineHeight: 1.5 }}
                        >
                            {planSponsorDisplayLine}
                        </Typography>
                    ) : null}
                    {vobGroupDisplayLine ? (
                        <Typography
                            variant="body1"
                            component="span"
                            test-id="vob-group-display"
                            sx={{ lineHeight: 1.5 }}
                        >
                            {vobGroupDisplayLine}
                        </Typography>
                    ) : null}
                    {employerDisplayLine ? (
                        <Typography
                            variant="body1"
                            component="span"
                            test-id="vob-employer-display"
                            sx={{ lineHeight: 1.5 }}
                        >
                            {employerDisplayLine}
                        </Typography>
                    ) : null}
                    <Typography
                        variant="body1"
                        component="span"
                        test-id="vob-member-id-display"
                        sx={{ lineHeight: 1.5 }}
                    >
                        Member ID: {memberId}
                    </Typography>
                </Box>

                {subscriberDisplayLine ? (
                    <Typography
                        variant="body1"
                        component="div"
                        test-id="vob-subscriber-display"
                        sx={{ mt: 0.5, lineHeight: 1.5 }}
                    >
                        {subscriberDisplayLine}
                    </Typography>
                ) : null}
            </Box>

            <Grid container spacing={2} alignItems="center">
                <Grid item xs sx={{ minWidth: 0 }}>
                    <RateAsSelector
                        rateAsPayorPlan={rateAsPayorPlan}
                        vobRateAsPayorPlan={vobRateAsPayorPlan}
                        facilityPayersState={facilityPayersState}
                        payersMap={payersMap}
                        selectedFacilityPlanId={selectedRateAsFacilityPlanId ?? undefined}
                        selectedFacility={selectedFacility}
                        vobFacilityId={currentVob?.facility?.facilityId != null ? Number(currentVob.facility.facilityId) : undefined}
                        onRateAsPayorPlanChange={handleRateAsPayorPlanChangeTabOnly}
                        readOnly={isReadOnlyAfterConvert}
                    />
                </Grid>
                <Grid item sx={{ minWidth: headerActionsWidthPx > 0 ? headerActionsWidthPx + 16 : 216, flexShrink: 0, flexGrow: 0, width: 'auto' }} />
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                    <DatePicker
                        test-id="vob-effective-from-date"
                        label="Effective From"
                        value={effectiveFromDate}
                        onChange={handleEffectiveFromDateChange}
                        error={dateErrors.effectiveFrom}
                        disabled={isReadOnlyAfterConvert}
                        className={`state-field ${dateErrors.effectiveFrom ? 'error-border' : 'required-fields'}`}
                    />
                </Grid>
                <Grid item xs={3}>
                    <DatePicker
                        test-id="vob-effective-to-date"
                        label="Effective To"
                        value={effectiveToDate}
                        onChange={handleEffectiveToDateChange}
                        error={dateErrors.effectiveTo}
                        disabled={isReadOnlyAfterConvert}
                        className={`state-field ${dateErrors.effectiveTo ? 'error-border' : 'required-fields'}`}
                    />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                        <FormControlLabel
                            test-id="vob-active-policy-checkbox"
                            control={
                                <Checkbox
                                    test-id="vob-active-policy-checkbox-input"
                                    checked={activePolicy}
                                    disabled={isReadOnlyAfterConvert}
                                    color={activePolicyError ? 'error' : 'primary'}
                                    onChange={(e) => setActivePolicy(e.target.checked)}
                                />
                            }
                            label="Active Policy"
                            sx={
                                activePolicyError
                                    ? {
                                          color: 'error.main',
                                          '& .MuiFormControlLabel-label': {
                                              color: 'error.main',
                                          },
                                      }
                                    : undefined
                            }
                        />
                        {activePolicyError && (
                            <Grid
                                container
                                className="error-message"
                                test-id="vob-active-policy-error-icon"
                                sx={{ color: 'error.main', mt: 0.5, flexWrap: 'nowrap' }}
                            >
                                <Grid item>
                                    <WarningIcon sx={{ fontSize: '2.2rem', mr: 1 }} />
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                    <FormControlLabel
                        test-id="vob-carryover-checkbox"
                        control={
                            <Checkbox
                                test-id="vob-carryover-checkbox-input"
                                checked={carryover}
                                disabled={isReadOnlyAfterConvert}
                                onChange={(e) => setCarryover(e.target.checked)}
                            />
                        }
                        label="4th Quarter Carryover"
                    />
                </Grid>
            </Grid>

            <NetworkTypeSelector
                networkType={networkType}
                memberType={memberType}
                onNetworkTypeChange={handleNetworkTypeChange}
                onMemberTypeChange={handleMemberTypeChange}
            />

            <VOBFormFields
                formData={formData}
                onInputChange={handleInputChange}
                currentVob={currentVob}
                deductibleError={deductibleError}
                remainingError={remainingError}
                oopmError={oopmError}
                oopmRemainingError={oopmRemainingError}
                deductibleErrorMessage={deductibleErrorMessage}
                remainingErrorMessage={remainingErrorMessage}
                oopmErrorMessage={oopmErrorMessage}
                oopmRemainingErrorMessage={oopmRemainingErrorMessage}
                copayError={copayError}
                maxDaysError={maxDaysError}
                coinsuranceError={coinsuranceError}
                copayErrorMessage={copayErrorMessage}
                maxDaysErrorMessage={maxDaysErrorMessage}
                coinsuranceErrorMessage={coinsuranceErrorMessage}
                resetButtonStates={resetButtonStates}
                setButtonStates={buttonStates}
                readOnly={isReadOnlyAfterConvert}
            />

            <LevelsOfCare
                levels={levelsOfCare}                
                networkType={networkType}
                memberType={memberType}
                setSavedLOCDataMap={setSavedLOCDataMap}
                savedLOCDataMap={savedLOCDataMap}
                savedFormDataMap={savedFormDataMap}
                levelErrors={levelErrors}
                readOnly={isReadOnlyAfterConvert}
            />

            <Box test-id="vob-form-bottom-actions" sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <AccessControl 
                    rolePageName={RolePageNames.Estimates} 
                    actionName="PDF Download"
                    renderNoAccess={() => null}
                >
                    <Button 
                        test-id="vob-download-pdf-button"
                        variant="contained"
                        color="primary"
                        style={{marginRight: "6px"}}
                        onClick={handleDownloadPdf}
                        disabled={!currentVob?.vobId || isSaving}
                        startIcon={<PictureAsPdfIcon />}
                    >
                        Download PDF
                    </Button>
                </AccessControl>
                {!isReadOnlyAfterConvert && (
                    <FPButton 
                        test-id="vob-save-button-bottom"
                        onClick={handleSaveVOB}
                        disabled={isSaving}
                        // startIcon={isSaving ? <CircularProgress size={20} /> : null}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </FPButton>
                )}
            </Box>

            <AvailityErrorDialog
                open={showAvailityErrorDialog}
                onClose={() => setShowAvailityErrorDialog(false)}
                availityErrorMessage={availityErrorMessage}
                selectedPatient={selectedPatient}
                selectedPatientCurrentInsurance={selectedPatientCurrentInsurance}
                isRunningAvailityVOB={isRunningAvailityVOB}
                onRunAvailityVob={runAvailityVobWithOverrides}
            />
        </Box>
    );
}

export default VOBForm;